import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Button, Card, Hairline, Input, Select, StatusPill } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { StaffDTO, StaffRole } from "@wmart/shared-types";
import { StaffRoleValues, staffRoleLabel } from "@wmart/shared-types";

interface FormValues {
  email: string;
  name: string;
  staff_role: StaffRole;
  department: string;
}

/**
 * Create (invite) + edit a staff member. Mirrors CompanyEdit's new-vs-edit
 * discrimination via useParams().id.
 *
 * Create is invite-based: the admin enters email + role only (no password).
 * The API emails the new staffer a set-password link. Edit changes
 * role/department, and offers Deactivate (soft-delete) + Resend invite (for
 * staff who haven't accepted yet). All mutations are wmart_admin-only on the
 * API — non-admins get a 403 toast.
 */
export function TeamEdit() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();

  const [staff, setStaff] = useState<StaffDTO | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      email: "",
      name: "",
      staff_role: "wmart_sales",
      department: "",
    },
  });

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.get<StaffDTO>(`/v1/admin/staff/${id}`);
        if (cancelled) return;
        setStaff(data);
        reset({
          email: data.email ?? "",
          name: data.name ?? "",
          staff_role: data.staff_role,
          department: data.department ?? "",
        });
      } catch (err) {
        if (cancelled) return;
        setLoadError(true);
        if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
        else notify("error", "Failed to load staff member");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function onSubmit(values: FormValues) {
    try {
      if (isNew) {
        const body: Record<string, unknown> = {
          email: values.email.trim(),
          staff_role: values.staff_role,
        };
        const name = values.name.trim();
        if (name) body.name = name;
        const dept = values.department.trim();
        if (dept) body.department = dept;
        const res = await api.post<{ staff_id: string }>(
          "/v1/admin/staff",
          body,
        );
        notify("success", "Invite sent — set-password email on its way");
        navigate(`/team/${res.staff_id}`, { replace: true });
      } else {
        const body = {
          staff_role: values.staff_role,
          // Empty department clears it (null); the API accepts nullable.
          department: values.department.trim() || null,
        };
        const updated = await api.patch<StaffDTO>(
          `/v1/admin/staff/${id}`,
          body,
        );
        setStaff(updated);
        notify("success", "Staff member updated");
      }
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Save failed");
    }
  }

  async function onDeactivate() {
    if (!id) return;
    if (
      !window.confirm(
        "Deactivate this staff member? They lose admin access immediately. This can be reversed only by engineering.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/v1/admin/staff/${id}`);
      notify("success", "Staff member deactivated");
      navigate("/team", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Deactivate failed");
    } finally {
      setBusy(false);
    }
  }

  async function onResendInvite() {
    if (!id) return;
    setBusy(true);
    try {
      await api.post(`/v1/admin/staff/${id}/resend-invite`, {});
      notify("success", "Invite re-sent");
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Resend failed");
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return <p className="p-2 text-body-sm text-ink-500">Staff member not found.</p>;
  }
  if (!isNew && !staff) {
    return <p className="p-2 text-body-sm text-ink-500">Loading…</p>;
  }

  const isDeactivated = !!staff?.deleted_at;

  return (
    <div className="flex max-w-xl flex-col gap-6">
      <Card as="section" className="p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-title-lg text-ink-900">
            {isNew ? "Add staff member" : "Edit staff member"}
          </h2>
          {staff?.invite_pending && !isDeactivated ? (
            <StatusPill tone="warning">Invite pending</StatusPill>
          ) : null}
          {isDeactivated ? (
            <StatusPill tone="neutral">Deactivated</StatusPill>
          ) : null}
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="mt-4 flex flex-col gap-4"
        >
          <Input
            type="email"
            label="Email"
            disabled={!isNew}
            autoComplete="off"
            {...register("email", { required: isNew, maxLength: 320 })}
          />
          <Input
            type="text"
            label="Name (optional)"
            {...register("name", { maxLength: 200 })}
          />
          <Select
            label="Role"
            disabled={isDeactivated}
            {...register("staff_role", { required: true })}
          >
            {StaffRoleValues.map((r) => (
              <option key={r} value={r}>
                {staffRoleLabel(r)}
              </option>
            ))}
          </Select>
          <Input
            type="text"
            label="Department (optional)"
            disabled={isDeactivated}
            {...register("department", { maxLength: 100 })}
          />

          <Hairline />

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={isSubmitting || isDeactivated}
              loading={isSubmitting}
            >
              {isNew ? "Send invite" : "Save"}
            </Button>
            {!isNew && staff?.invite_pending && !isDeactivated ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => void onResendInvite()}
                disabled={busy}
              >
                Resend invite
              </Button>
            ) : null}
          </div>
        </form>
      </Card>

      {!isNew && !isDeactivated ? (
        <Card as="section" className="p-6">
          <h3 className="font-serif text-title-md text-ink-900">Danger zone</h3>
          <p className="mt-2 text-body-sm text-ink-500">
            Deactivating revokes this staff member's access immediately and ends
            their sessions.
          </p>
          <div className="mt-4">
            <Button
              type="button"
              variant="danger"
              onClick={() => void onDeactivate()}
              disabled={busy}
            >
              Deactivate
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
